import React, { useEffect, useState, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import { integrationAPI } from '../api';
import ExchangeJournal from '../components/ExchangeJournal';
import toast from 'react-hot-toast';
import { confirmToast, dangerConfirm } from '../components/ConfirmToast';

const Card = ({ children, style = {} }) => (
  <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius, 12px)', padding: 20, ...style }}>
    {children}
  </div>
);

const Stat = ({ label, value, color }) => (
  <Card style={{ flex: 1, minWidth: 130 }}>
    <div style={{ color: 'var(--dim)', fontSize: 12, marginBottom: 6 }}>{label}</div>
    <div style={{ color: color || 'var(--text)', fontSize: 28, fontWeight: 700 }}>{value}</div>
  </Card>
);

// Как документ называется на языке того, кто его ищет. То же, что в журнале
// на сервере: очередь и лента должны звать одно и то же одинаково.
const ENTITY_NAMES = {
  client:           'Контрагенты',
  nomenclature:     'Номенклатура',
  sale:             'Отгрузки',
  production:       'Выпуск',
  production_shift: 'Выпуск за смену',
  dsp_defect:       'Брак ДСП',
  film_defect:      'Брак плёнки',
  stock_delta:      'Изменение остатков',
  receipt_cancel:   'Отмена прихода',
  transfer_cancel:  'Отмена перемещения',
};

const STATUS_COLOR = {
  pending: 'var(--warning)', sent: 'var(--success)', failed: 'var(--danger)',
  ok: 'var(--success)', duplicate: 'var(--dim)', error: 'var(--danger)',
};

const Badge = ({ s }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
    color: STATUS_COLOR[s] || 'var(--muted)', background: 'var(--bg)', border: '1px solid var(--border)',
  }}>{s}</span>
);

const Toggle = ({ on, onClick }) => (
  <button onClick={onClick} style={{
    width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', flexShrink: 0,
    background: on ? 'var(--success)' : 'var(--border)', position: 'relative', transition: 'background .3s',
  }}>
    <div style={{
      width: 20, height: 20, background: '#fff', borderRadius: '50%',
      position: 'absolute', top: 2, left: on ? 22 : 2, transition: 'left .3s', boxShadow: '0 2px 4px rgba(0,0,0,.2)',
    }} />
  </button>
);

const inputStyle = {
  width: '100%', padding: '9px 12px', background: 'var(--input)',
  border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

export default function IntegrationPage() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({ onec_base_url: '', onec_token: '', is_active: false,
    push_enabled: false, shift_start_hour: 0, hold_open_shift: true,
    stock_from_1c_only: false,
    send_low_grades: false, send_defects: false,
    send_production: true });
  const [saving, setSaving] = useState(false);
  // Настройки закрыты: их задают один раз, а очередь смотрят каждый день
  const [cfgOpen, setCfgOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await integrationAPI.getDashboard();
      setData(data);
      setCfg(c => ({ ...c, ...data.config, onec_token: '' }));
    } catch (e) {
      toast.error(t("Не удалось загрузить данные интеграции"));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // har 10 soniyada yangilash
    return () => clearInterval(t);
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (!payload.onec_token) delete payload.onec_token; // bo'sh bo'lsa parolni o'zgartirmaymiz
      await integrationAPI.saveConfig(payload);
      toast.success(t("Настройки сохранены"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // Пустые смены: событие в очереди есть, а строк за ним ноль — задания той
  // смены удалили как тестовые. Убираем по кнопке, а не сами: очередь — это
  // то, что ещё не дошло до 1С, и молча из неё ничего пропадать не должно.
  const dropEmpty = async () => {
    const n = (data?.empty_shifts || []).length;
    if (!window.confirm(
      `Убрать из очереди пустые смены (${n})? В 1С по ним нечего выгружать.`)) return;
    try {
      const r = await integrationAPI.dropEmptyShifts();
      toast.success(`Убрано из очереди: ${r.data.removed}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось убрать');
    }
  };

  // Очередь — не архив. Строку можно удалить, базу откатить, событие могло не
  // встать из-за сбоя — и тогда смена отработана, а документа в 1С нет. Здесь
  // мы спрашиваем не очередь, а саму работу: у каждой ли смены есть событие.
  const requeue = async () => {
    const n = (data?.missing || []).length;
    if (!window.confirm(
      `Вернуть в очередь ${n} невыгруженных? 1С перезаполнит свои черновики, ` +
      `второго документа не появится.`)) return;
    try {
      const r = await integrationAPI.requeueMissing();
      toast.success(`Поставлено в очередь: ${r.data.queued}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось поставить в очередь');
    }
  };

  // Отменить лишнее в очереди.
  //
  // Очередь копится не только из работы цеха: каталог, уехавший обратно в 1С
  // кругом, смены, удалённые как тестовые, события за месяцы, пока обмена ещё
  // не было. Отправлять это в настоящую 1С нельзя, а чистить через консоль —
  // значит ждать того, кто умеет в неё ходить.
  //
  // Событие не удаляем, а помечаем «не отправляли»: в 1С оно не уходило, и
  // журнал не должен говорить «принято в 1С». Ошиблись — «вернуть» ставит
  // обратно в очередь.
  const cancelQueue = async (body, what, count) => {
    if (!await confirmToast(
      `${what} — ${count} шт.

`
      + 'В 1С они не уйдут. В журнале останутся как «не отправляли», '
      + 'вернуть в очередь можно тем же местом.',
      { title: 'Отменить в очереди?', confirmText: 'Да, отменить',
        danger: true, big: true, countdown: count > 100 ? 5 : 0 })) return;
    try {
      const r = await integrationAPI.skipOutbox(body);
      toast.success(`Отменено: ${r.data.skipped}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось отменить');
    }
  };

  // Граница рабочих суток — то же «сегодня», что во всех отчётах завода.
  // Всё, что раньше неё, накопилось до подключения; всё, что позже, — работа
  // сегодняшней смены, и её трогать незачем.
  const shiftStart = () => {
    const d = new Date();
    if (d.getHours() < 8) d.setDate(d.getDate() - 1);
    d.setHours(8, 0, 0, 0);
    return d;
  };

  const cancelOld = () => {
    const from = shiftStart();
    cancelQueue({ before: from.toISOString() },
      `Всё, что встало в очередь до ${from.toLocaleDateString('ru-RU')} 8:00`,
      s.outbox_pending ?? 0);
  };

  const restoreCancelled = async () => {
    if (!await confirmToast(
      'Отменённые события вернутся в очередь и уйдут в 1С при ближайшем обмене.',
      { title: 'Вернуть отменённое?', confirmText: 'Да, вернуть', big: true })) return;
    try {
      const r = await integrationAPI.skipOutbox({ restore: true, all: true });
      toast.success(`Возвращено в очередь: ${r.data.restored}`
        + (r.data.kept_closed
          ? `. Не возвращено ${r.data.kept_closed}: дни старше вчерашнего бухгалтерия ведёт сама`
          : ''));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось вернуть');
    }
  };

  const s = data?.stats || {};

  const Row = ({ children }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      background: 'var(--bg)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text)' }}>
      {children}
    </div>
  );

  // Включение меняет числа на всех экранах разом, поэтому спрашиваем трижды
  // и с отсчётом — как у остальных необратимых действий.
  const switchOwner = async () => {
    const on = !cfg.stock_from_1c_only;
    if (on) {
      if (!await dangerConfirm({
        title: 'Остаток только по 1С',
        finalText: 'Да, включить',
        steps: [
          'Остаток на всех экранах будет считаться ТОЛЬКО по снимку из 1С.\n\n'
          + 'Собственные записи MES о выпуске и отгрузке из числа выйдут — они '
          + 'станут отдельной строкой «ждём 1С».',

          'Числа изменятся сразу и у всех: ЛДСП пойдёт вниз (уходит двойной '
          + 'счёт), сырьё вверх (уходят наши списания, которых 1С ещё не '
          + 'видела). Это исправление, а не потеря.',

          'После включения остаток живёт от обмена до обмена. Встанет обмен — '
          + 'склад будет видеть прежние числа; про это предупреждает строка '
          + '«Остаток из 1С» у склада и у менеджера.',
        ],
      })) return;
    }
    setCfg(c => ({ ...c, stock_from_1c_only: on }));
  };

  return (
    <Layout title={t("1С Интеграция")}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: 0 }}>{t('🔗 Интеграция с 1С:Бухгалтерия')}</h2>
          <button onClick={load} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}>{t('↻ Обновить')}</button>
        </div>

        {/* Статистика */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Stat label="В очереди (→1С)" value={s.outbox_pending ?? '—'} color="var(--warning)" />
          <Stat label="Отправлено" value={s.outbox_sent ?? '—'} color="var(--success)" />
          <Stat label="Ошибки" value={s.outbox_failed ?? '—'} color="var(--danger)" />
          <Stat label="Входящие (1С→)" value={s.inbox_total ?? '—'} />
          <Stat label="Сопоставлено" value={s.mapped ?? '—'} />
        </div>

        {/* Из чего очередь состоит — и чем её чистить.
            «В очереди 7116» не говорит ничего: одно дело семь тысяч выпусков,
            другое — семь тысяч карточек каталога, уехавших обратно в 1С
            кругом. Второе в настоящую 1С отправлять нельзя, и до сих пор
            убрать это можно было только из консоли. */}
        {(s.outbox_by_entity || []).length > 0 && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                          flexWrap: 'wrap', marginBottom: 10 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, margin: 0, flex: 1 }}>
                {t('Что стоит в очереди')}
              </h3>
              <button onClick={cancelOld} style={{ ...inputStyle, width: 'auto',
                cursor: 'pointer', color: 'var(--warning)', fontWeight: 600, fontSize: 13 }}>
                {t('Отменить всё до 8:00')}
              </button>
              <button onClick={restoreCancelled} style={{ ...inputStyle, width: 'auto',
                cursor: 'pointer', color: 'var(--dim)', fontSize: 13 }}>
                {t('Вернуть отменённое')}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(s.outbox_by_entity || []).map(x => (
                <span key={x.entity} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 20, padding: '5px 6px 5px 14px', fontSize: 13,
                  color: 'var(--text)',
                }}>
                  {ENTITY_NAMES[x.entity] || x.entity}
                  <b style={{ color: 'var(--warning)' }}>{x.n}</b>
                  {/* Крестик отменяет весь этот вид разом: чистят очередь
                      именно так — «каталог не отправлять», а не по одному. */}
                  <button
                    onClick={() => cancelQueue({ entity: x.entity },
                      ENTITY_NAMES[x.entity] || x.entity, x.n)}
                    title={t('Отменить все такие события в очереди')}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--danger)', fontSize: 14, lineHeight: 1,
                      padding: '2px 6px', fontFamily: 'inherit',
                    }}>✕</button>
                </span>
              ))}
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10 }}>
              Отменённое из 1С не пропадает — его там и не было. В журнале оно
              останется строкой «не отправляли».
            </div>
          </Card>
        )}

        {/* Настройки. Адрес 1С, токен и час начала смены задают один раз при
            запуске и потом не трогают полгода — а занимали они пол-экрана над
            тем, ради чего сюда заходят каждый день: что застряло в очереди и
            что не ушло. Прячем под кнопку. */}
        <Card>
          <button onClick={() => setCfgOpen(o => !o)} style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontFamily: 'inherit', textAlign: 'left',
          }}>
            <h3 style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700,
                         margin: 0, flex: 1 }}>{t('⚙️ Настройки')}</h3>
            <span style={{ color: 'var(--dim)', fontSize: 12 }}>
              {cfg.is_active ? t('обмен включён') : t('обмен выключен')}
            </span>
            <span style={{ color: 'var(--dim)', fontSize: 15 }}>{cfgOpen ? '▾' : '▸'}</span>
          </button>

          <div style={{ display: cfgOpen ? 'flex' : 'none',
                        flexDirection: 'column', gap: 12, marginTop: 14 }}>
            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Интеграция включена')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Формировать очередь событий для 1С')}</div>
              </div>
              <Toggle on={cfg.is_active} onClick={() => setCfg(c => ({ ...c, is_active: !c.is_active }))} />
            </Row>
            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Активная отправка в 1С (push)')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Иначе 1С сама забирает из очереди (pull)')}</div>
              </div>
              <Toggle on={cfg.push_enabled} onClick={() => setCfg(c => ({ ...c, push_enabled: !c.push_enabled }))} />
            </Row>

            {/* Когда выпуск попадает в 1С. По учёту правильно ждать конца
                смены: бухгалтер получает готовый отчёт за сутки, а не
                половину смены, которая потом молча изменится под ним. Но пока
                обмен налаживают, ждать до завтра нечем — надо сделать
                операцию и тут же посмотреть, дошёл ли документ. */}
            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Ждать конца смены')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12 }}>
                  {cfg.hold_open_shift
                    ? t('Выпуск и брак уходят в 1С одним отчётом после конца смены')
                    : t('Выпуск и брак уходят в 1С сразу — документ дописывается по ходу смены')}
                </div>
              </div>
              <Toggle on={cfg.hold_open_shift}
                onClick={() => setCfg(c => ({ ...c, hold_open_shift: !c.hold_open_shift }))} />
            </Row>

            {/* Бухгалтерия вносит выпуск сама: повторная выгрузка распроводит
                документ в 1С и стирает её правки. Включено по умолчанию. */}
            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Создавать в 1С отчёт производства')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12, maxWidth: 620, lineHeight: 1.55 }}>
                  {cfg.send_production
                    ? t('Выпуск за смену уходит в 1С отчётом производства. Если документ за этот день в 1С уже проведён вручную, MES его не трогает')
                    : t('Не уходит: отчёт производства. В журнале выпуск остаётся как «Не отправляли»; бухгалтерия вносит его в 1С сама')}
                </div>
              </div>
              <Toggle on={!!cfg.send_production}
                onClick={() => setCfg(c => ({ ...c, send_production: !c.send_production }))} />
            </Row>

            {/* Что бухгалтерия принимает: 1С отклоняет строки низших сортов
                (Сорта продукции не сопоставлено), а брак плёнки и ДСП
                расширение больше не проводит. Поэтому оба выключены. */}
            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Отправлять в 1С низшие сорта')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12 }}>
                  {cfg.send_low_grades
                    ? t('Выпуск низших сортов и их расход уходят в 1С')
                    : t('Не уходит: строки низших сортов и их расход плёнки и ДСП. В 1С едет только высший сорт')}
                </div>
              </div>
              <Toggle on={!!cfg.send_low_grades}
                onClick={() => setCfg(c => ({ ...c, send_low_grades: !c.send_low_grades }))} />
            </Row>

            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Отправлять в 1С брак плёнки и брак ДСП')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12, maxWidth: 620, lineHeight: 1.55 }}>
                  {cfg.send_defects
                    ? t('Брак плёнки и брак ДСП уходят в 1С')
                    : t('Не уходит: брак плёнки и брак ДСП. В журнале они остаются как «Не отправляли»; после включения верните их кнопкой «Вернуть отменённое»')}
                </div>
              </div>
              <Toggle on={!!cfg.send_defects}
                onClick={() => setCfg(c => ({ ...c, send_defects: !c.send_defects }))} />
            </Row>

            {/* Кто хозяин остатка.
                Пока выключено, MES складывает свой счёт со снимком из 1С, и
                один и тот же лист попадает в остаток дважды: своей записью о
                выпуске и снимком, в который он вернулся, пройдя круг
                MES -> 1С -> MES. На заводе это дало 33 069 листов там, где
                физически лежит 15 773. */}
            <Row>
              <div>
                <div style={{ fontWeight: 600 }}>{t('Остаток считать только по 1С')}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12, maxWidth: 620, lineHeight: 1.55 }}>
                  {cfg.stock_from_1c_only
                    ? 'Остаток — только снимок 1С. Выпуск и отгрузка MES идут отдельной строкой «ждём 1С» и в число не входят.'
                    : 'Сейчас к снимку 1С прибавляется собственный счёт MES — один лист может попасть в остаток дважды.'}
                  {cfg.last_stock_at && (
                    <><br />Последний снимок: {new Date(cfg.last_stock_at).toLocaleString('ru')}</>
                  )}
                </div>
              </div>
              <Toggle on={cfg.stock_from_1c_only} onClick={switchOwner} />
            </Row>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{t('Адрес 1С (HTTP-сервис)')}</label>
                <input style={inputStyle} placeholder="http://1c-server/base/hs/mes/v1"
                  value={cfg.onec_base_url} onChange={e => setCfg(c => ({ ...c, onec_base_url: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>
                  Токен доступа {data?.config?.has_password ? '(задан — пусто = не менять)' : ''}
                </label>
                <input style={inputStyle} type="password" placeholder="••••••••"
                  value={cfg.onec_token} onChange={e => setCfg(c => ({ ...c, onec_token: e.target.value }))} />
              </div>
            </div>

            {/* Учёт посменный: выпуск уходит в 1С одним отчётом на смену.
                Час задаёт границу суток — работа, законченная до него, ложится
                во вчерашнюю дату, как её и считает производство. */}
            {/* Час начала смены задавался ЗДЕСЬ, отдельной настройкой для
                1С, и стоял в нуле — то есть по полуночи. Отчёты завода при
                этом резали сутки с восьми утра. Ночная смена третьего числа
                у директора принадлежала третьему целиком, а в документах 1С
                разваливалась пополам между третьим и четвёртым: бухгалтер и
                директор смотрели на одну дату и видели разное количество
                листов.

                Настройка теперь одна — цеховая, в разделе «Смены». Здесь
                только показываем, какая она, чтобы не заводить второй
                источник правды. */}
            <div style={{ maxWidth: 320 }}>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>
                {t('Час начала смены')}
              </label>
              <div style={{
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '9px 12px', color: 'var(--text)',
                fontSize: 14, fontWeight: 700,
              }}>
                {cfg.shift_start_hour ?? 8}:00 → {cfg.shift_start_hour ?? 8}:00
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                Берётся из настроек цеха — тот же час, по которому считают
                смены отчёты завода. Документы 1С и отчёты MES показывают за
                один день одно и то же.
              </div>
            </div>

            <div>
              <button onClick={save} disabled={saving} style={{
                ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: 700,
                background: 'var(--accent)', color: '#fff', border: 'none', padding: '10px 20px',
              }}>{saving ? 'Сохранение...' : 'Сохранить настройки'}</button>
            </div>
          </div>
        </Card>

        {/* Выпуск, ушедший в 1С без материалов: у продукции нет рецепта,
            значит нет ни состава выпуска (материалов), ни плановой цены. Ошибки при
            этом не видно — событие уходит успешно, поэтому и нужен отдельный
            блок. Заполнят рецепт — MES повторит выгрузку сам. */}
        {(data?.no_recipe || []).length > 0 && (
          <div style={{
            background: '#f59e0b14', border: '1px solid #f59e0b40',
            borderRadius: 12, padding: '14px 18px', marginBottom: 16,
          }}>
            <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 6 }}>
              ⚠ {t('Выпуск ушёл в 1С без материалов — не заполнен рецепт')}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10 }}>
              {t('По этим позициям в 1С не будет ни материалов в «Составе выпуска», ни плановой цены. Требования-накладные 1С с 08.10 не создаёт. Заполните рецепт — MES повторит выгрузку сам.')}
            </div>
            {data.no_recipe.slice(0, 10).map(r => (
              <div key={r.nomenclature_id} style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                fontSize: 13, color: 'var(--text)', padding: '3px 0',
              }}>
                <span>{r.name}</span>
                <span style={{ color: 'var(--dim)', whiteSpace: 'nowrap' }}>
                  {r.events} {t('соб.')}
                </span>
              </div>
            ))}
            {data.no_recipe.length > 10 && (
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 6 }}>
                {t('и ещё')} {data.no_recipe.length - 10}
              </div>
            )}
          </div>
        )}

        {/* Сделано в цеху, а в 1С не ушло. Раньше «выгружено» значило «лежит
            в очереди», и любая потеря была молчаливой: смена отработана,
            документа нет, узнают при сверке через месяц. */}
        {(data?.missing || []).length > 0 && (
          <div style={{
            background: '#ef444412', border: '1px solid #ef444455',
            borderRadius: 12, padding: '14px 18px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                          flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ color: '#ef4444', fontWeight: 700, flex: 1 }}>
                ⚠ {t('Сделано, но в 1С не ушло')} — {data.missing.length}
              </div>
              <button onClick={requeue} style={{
                ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: 700,
                padding: '8px 16px', background: '#ef4444', color: '#fff',
                border: 'none',
              }}>{t('Вернуть в очередь')}</button>
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10 }}>
              {t('Событий по этой работе в очереди нет и не было. 1С перезаполнит свои черновики — второго документа не появится.')}
            </div>
            {data.missing.slice(0, 12).map(m => (
              <div key={m.entity + m.local_id} style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                fontSize: 13, color: 'var(--text)', padding: '3px 0',
              }}>
                <span>{m.label}</span>
                <span style={{ color: 'var(--dim)' }}>{m.detail}</span>
              </div>
            ))}
            {data.missing.length > 12 && (
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 6 }}>
                {t('и ещё')} {data.missing.length - 12}
              </div>
            )}
          </div>
        )}

        {/* Пустые смены. Событие стоит в очереди, а выгружать по нему нечего:
            задания той смены удалили как тестовые. 1С такое молча пропускает,
            и они висят вечно — из-за них счётчик «в очереди» врёт, и настоящий
            затык за ними не виден. */}
        {(data?.empty_shifts || []).length > 0 && (
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '14px 18px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                          flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, flex: 1 }}>
                {t('Пустые смены в очереди')} — {data.empty_shifts.length}
              </div>
              <button onClick={dropEmpty} style={{
                ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: 700,
                padding: '8px 16px', color: 'var(--danger)',
                borderColor: 'var(--danger)', background: 'transparent',
              }}>{t('Убрать из очереди')}</button>
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10 }}>
              {t('Задания этих смен удалены — выгружать в 1С нечего. 1С их молча пропускает, и они висят в очереди навсегда.')}
            </div>
            {data.empty_shifts.map(e => (
              <div key={e.id} style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                fontSize: 13, color: 'var(--text)', padding: '3px 0',
              }}>
                <span>{e.entity}</span>
                <span style={{ color: 'var(--dim)' }}>{e.local_id}</span>
              </div>
            ))}
          </div>
        )}

        {/* Лента обмена словами — то, ради чего сюда заходят каждый день.
            Таблицы кодов ниже остаются: по ним чинят, а не читают. */}
        <ExchangeJournal />

        {/* Очередь + журнал */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card>
            <h3 style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>{t('📤 Очередь исходящих (MES → 1С)')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
              {(data?.outbox || []).map(o => (
                <Row key={o.id}>
                  <span style={{ fontWeight: 600 }}>{o.entity}</span>
                  <span style={{ color: 'var(--dim)' }}>{o.local_id}</span>
                  <Badge s={o.status} />
                  {/* Отменить одно событие: бывает, что лишний в очереди
                      ровно один документ, и отменять весь вид из-за него
                      значит выбросить нужное вместе с ним. */}
                  {['pending', 'failed'].includes(o.status) && (
                    <button
                      onClick={() => cancelQueue({ ids: [o.id] },
                        `${ENTITY_NAMES[o.entity] || o.entity} · ${o.local_id}`, 1)}
                      title={t('Отменить это событие')}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--danger)', fontSize: 13, padding: '0 2px',
                        fontFamily: 'inherit',
                      }}>✕</button>
                  )}
                </Row>
              ))}
              {(!data?.outbox || data.outbox.length === 0) && <div style={{ color: 'var(--dim)', fontSize: 13 }}>{t('Пусто')}</div>}
            </div>
          </Card>

          <Card>
            <h3 style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>{t('📥 Журнал входящих (1С → MES)')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
              {(data?.inbox || []).map(i => (
                <Row key={i.id}>
                  <span style={{ fontWeight: 600 }}>{i.entity}</span>
                  <span style={{ color: 'var(--dim)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140, whiteSpace: 'nowrap' }}>{i.external_id}</span>
                  <Badge s={i.status} />
                </Row>
              ))}
              {(!data?.inbox || data.inbox.length === 0) && <div style={{ color: 'var(--dim)', fontSize: 13 }}>{t('Пусто')}</div>}
            </div>
          </Card>
        </div>

        <div style={{ color: 'var(--dim)', fontSize: 12 }}>
          Обновляется автоматически каждые 10 сек. Основной режим — 1С забирает очередь (pull). Все документы в 1С создаются в статусе «Не проведён».
        </div>
      </div>
    </Layout>
  );
}
